/**
 * Schmankerl des Monats Component
 * Zeitbasierte Produktanzeige mit dynamischem Swiper.js Slider
 * Integriert in das DeSight Shopify X System
 */

class SchmankerDesMonats {
  constructor() {
    this.sections = document.querySelectorAll('.schmankerl-des-monats');
    this.swipers = new Map();
    this.debug = window.Shopify && window.Shopify.designMode;

    if (this.debug) {
      console.log('SchmankerDesMonats initialisiert. Gefundene Sections:', this.sections.length);
      this.sections.forEach((section, index) => {
        console.log(`Section ${index + 1}:`, {
          id: section.dataset.sectionId,
          layout: section.dataset.layout,
          productCount: section.dataset.productCount
        });
      });
    }

    this.init();
  }

  init() {
    // Warte auf DOM ready
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.initSections());
    } else {
      this.initSections();
    }

    // Shopify Section Events
    document.addEventListener('shopify:section:load', (event) => {
      if (event.target.classList.contains('schmankerl-des-monats-section')) {
        this.initSection(event.target.querySelector('.schmankerl-des-monats'));
      }
    });

    document.addEventListener('shopify:section:unload', (event) => {
      if (event.target.classList.contains('schmankerl-des-monats-section')) {
        this.destroySection(event.target.querySelector('.schmankerl-des-monats'));
      }
    });
  }

  initSections() {
    this.sections.forEach(section => this.initSection(section));
  }

  initSection(section) {
    if (!section) {
      console.warn('Section ist null oder undefined');
      return;
    }

    const sectionId = section.dataset.sectionId || `section-${Date.now()}`;
    const layout = section.dataset.layout || 'grid';
    const productCount = parseInt(section.dataset.productCount) || 0;

    console.log('Initialisiere Section:', sectionId, 'Layout:', layout, 'Produkte:', productCount);

    // Loading-State hinzufügen
    const swiperContainer = section.querySelector('.schmankerl-swiper');
    if (swiperContainer) {
      swiperContainer.classList.add('loading');
    }

    // Parallax-Effekt initialisieren
    this.initParallax(section);

    // Slider nur bei mehreren Produkten und Slider-Layout initialisieren
    if (layout === 'slider' && productCount > 1) {
      // Kleine Verzögerung für DOM-Stabilität
      setTimeout(() => {
        this.initSlider(section, sectionId);
      }, 100);
    } else if (layout === 'slider' && productCount <= 1) {
      // Bei nur einem Produkt: Statisches Layout verwenden
      this.showStaticLayout(section);
    } else {
      // Kein Slider nötig, Loading-State entfernen
      if (swiperContainer) {
        swiperContainer.classList.remove('loading');
      }
    }

    // Accessibility improvements
    this.enhanceAccessibility(section);
  }

  initSlider(section, sectionId) {
    // Prüfe ob Swiper bereits geladen ist
    if (typeof Swiper === 'undefined') {
      this.loadSwiper().then(() => {
        this.createSwiper(section, sectionId);
      }).catch((error) => {
        if (this.debug) {
          console.error('Fehler beim Laden von Swiper:', error);
        }
        // Fallback: Zeige Produkte ohne Slider an
        this.showStaticLayout(section);
      });
    } else {
      this.createSwiper(section, sectionId);
    }
  }

  async loadSwiper() {
    // Lade Swiper CSS
    if (!document.querySelector('link[href*="swiper"]')) {
      const swiperCSS = document.createElement('link');
      swiperCSS.rel = 'stylesheet';
      swiperCSS.href = 'https://cdn.jsdelivr.net/npm/swiper@11/swiper-bundle.min.css';
      document.head.appendChild(swiperCSS);
    }

    // Lade Swiper JS
    if (typeof Swiper === 'undefined') {
      return new Promise((resolve, reject) => {
        const script = document.createElement('script');
        script.src = 'https://cdn.jsdelivr.net/npm/swiper@11/swiper-bundle.min.js';
        script.onload = () => {
          // Warte kurz, damit Swiper vollständig initialisiert ist
          setTimeout(resolve, 100);
        };
        script.onerror = reject;
        document.head.appendChild(script);
      });
    }
    return Promise.resolve();
  }

  showStaticLayout(section) {
    // Fallback: Zeige alle Produkte in einem statischen Grid
    const swiperContainer = section.querySelector('.schmankerl-swiper');
    if (swiperContainer) {
      // Loading-State entfernen
      swiperContainer.classList.remove('loading');

      // Entferne Swiper-spezifische Klassen
      swiperContainer.classList.remove('swiper');
      swiperContainer.classList.add('schmankerl-static-grid');

      // Grid-Layout anwenden
      swiperContainer.style.display = 'grid';
      swiperContainer.style.gridTemplateColumns = 'repeat(auto-fit, minmax(300px, 1fr))';
      swiperContainer.style.gap = '20px';
      swiperContainer.style.width = '100%';

      const wrapper = swiperContainer.querySelector('.swiper-wrapper');
      if (wrapper) {
        wrapper.style.display = 'contents';
        wrapper.classList.add('schmankerl-static-wrapper');
      }

      const slides = swiperContainer.querySelectorAll('.swiper-slide');
      slides.forEach(slide => {
        slide.style.width = 'auto';
        slide.style.height = 'auto';
        slide.style.transform = 'none';
        slide.style.margin = '0';
        slide.classList.add('schmankerl-static-slide');
      });

      // Navigation und Pagination verstecken
      const navigation = section.querySelectorAll('.swiper-button-prev, .swiper-button-next, .swiper-pagination');
      navigation.forEach(nav => {
        nav.style.display = 'none';
      });

      if (this.debug) {
        console.log('Statisches Layout aktiviert für Section:', section.dataset.sectionId);
      }
    }
  }

  createSwiper(section, sectionId) {
    const swiperContainer = section.querySelector('.schmankerl-swiper');
    if (!swiperContainer) {
      console.warn('Swiper Container nicht gefunden in Section:', sectionId);
      return;
    }

    // Prüfe ob Swiper verfügbar ist
    if (typeof Swiper === 'undefined') {
      console.error('Swiper ist nicht verfügbar');
      this.showStaticLayout(section);
      return;
    }

    // Bestehenden Swiper zerstören falls vorhanden
    if (this.swipers.has(sectionId)) {
      this.swipers.get(sectionId).destroy(true, true);
    }

    // Slider-Einstellungen aus Section-Attributen lesen
    const slidesPerView = parseInt(swiperContainer.dataset.slidesPerView) || 1;
    const autoplay = swiperContainer.dataset.autoplay === 'true';
    const autoplayDelay = parseInt(swiperContainer.dataset.autoplayDelay) || 4000;

    // Prüfe ob genügend Slides vorhanden sind
    const slides = swiperContainer.querySelectorAll('.swiper-slide');
    if (slides.length === 0) {
      console.warn('Keine Slides gefunden in Swiper Container');
      return;
    }

    // Prüfe Navigation-Buttons - KORRIGIERTE Selektoren
    const sliderContainer = section.querySelector('.schmankerl-des-monats__products--slider');

    // Versuche verschiedene Selektoren für die Navigation-Buttons
    let nextButton = sliderContainer ? sliderContainer.querySelector('.schmankerl-swiper-button-next') : null;
    let prevButton = sliderContainer ? sliderContainer.querySelector('.schmankerl-swiper-button-prev') : null;

    // Fallback: Suche auch mit Standard Swiper-Klassen
    if (!nextButton) {
      nextButton = sliderContainer ? sliderContainer.querySelector('.swiper-button-next') : null;
    }
    if (!prevButton) {
      prevButton = sliderContainer ? sliderContainer.querySelector('.swiper-button-prev') : null;
    }

    if (this.debug) {
      console.log('Navigation Buttons gefunden:', {
        nextButton: !!nextButton,
        prevButton: !!prevButton,
        slidesCount: slides.length,
        sectionId: sectionId,
        nextButtonSelector: nextButton ? nextButton.className : 'nicht gefunden',
        prevButtonSelector: prevButton ? prevButton.className : 'nicht gefunden',
        swiperContainer: swiperContainer.className,
        sliderContainer: sliderContainer ? sliderContainer.className : 'nicht gefunden'
      });
    }

    // Swiper-Konfiguration - responsive Auto-Height
    const swiperConfig = {
      slidesPerView: 1,
      spaceBetween: 0,
      loop: slides.length > 1,
      grabCursor: true,
      centeredSlides: true,
      watchOverflow: true,
      allowTouchMove: true,
      simulateTouch: true,
      autoHeight: window.innerWidth <= 749, // Auto-Height nur auf Mobile
      updateOnWindowResize: true,
      observer: true,
      observeParents: true,

      // Verbesserte Navigation-Konfiguration
      navigation: nextButton && prevButton ? {
        nextEl: nextButton,
        prevEl: prevButton,
        disabledClass: 'swiper-button-disabled',
        hideOnClick: false,
        lockClass: 'swiper-button-lock',
      } : false,

      // Pagination - verbesserte Konfiguration wie collection-list-modern
      ...(sliderContainer && sliderContainer.querySelector('.schmankerl-swiper-pagination') && {
        pagination: {
          el: sliderContainer.querySelector('.schmankerl-swiper-pagination'),
          clickable: true,
          dynamicBullets: false, // Deaktiviert für konsistente Strip-Darstellung
          type: 'bullets',
          bulletClass: 'swiper-pagination-bullet',
          bulletActiveClass: 'swiper-pagination-bullet-active',
          modifierClass: 'swiper-pagination-',
          lockClass: 'swiper-pagination-lock',
          hideOnClick: false,
        }
      }),

      // Autoplay
      ...(autoplay && {
        autoplay: {
          delay: autoplayDelay,
          disableOnInteraction: false,
          pauseOnMouseEnter: true,
          reverseDirection: false,
          stopOnLastSlide: false,
        }
      }),

      // Accessibility
      a11y: {
        enabled: true,
        prevSlideMessage: 'Vorheriges Produkt',
        nextSlideMessage: 'Nächstes Produkt',
        paginationBulletMessage: 'Gehe zu Produkt {{index}}',
        slideLabelMessage: 'Produkt {{index}} von {{slidesLength}}',
      },

      // Keyboard Navigation
      keyboard: {
        enabled: true,
        onlyInViewport: true,
        pageUpDown: true,
      },

      // Mouse wheel control
      mousewheel: {
        enabled: false, // Deaktiviert für bessere UX
      },

      // Events
      on: {
        init: (swiper) => {
          console.log('Swiper initialisiert:', sectionId, 'Slides:', swiper.slides.length);
          console.log('Navigation Buttons nach Init:', {
            next: swiper.navigation?.nextEl,
            prev: swiper.navigation?.prevEl,
            nextEnabled: swiper.allowSlideNext,
            prevEnabled: swiper.allowSlidePrev,
            navigationEnabled: swiper.navigation?.enabled
          });



          // Sofortige Überprüfung der Navigation
          if (swiper.navigation && nextButton && prevButton) {
            console.log('Navigation erfolgreich initialisiert');
            // Teste Navigation direkt nach Init
            setTimeout(() => {
              this.testNavigation(swiper, sectionId);
            }, 100);
          } else {
            console.warn('Navigation nicht korrekt initialisiert, aktiviere Fallback');
            this.setupNavigationFallback(swiper, nextButton, prevButton, sectionId);
          }

          this.updateSliderAccessibility(section);
        },
        slideChange: (swiper) => {
          console.log('Slide geändert:', sectionId, 'Aktiver Index:', swiper.activeIndex);
          this.updateSliderAccessibility(section);
        },
        navigationNext: (swiper) => {
          console.log('Navigation Next Event ausgelöst:', sectionId, 'Neuer Index:', swiper.activeIndex);
        },
        navigationPrev: (swiper) => {
          console.log('Navigation Prev Event ausgelöst:', sectionId, 'Neuer Index:', swiper.activeIndex);
        },
        click: (swiper, event) => {
          console.log('Swiper Click Event:', event.target.className);
        },
        beforeDestroy: (swiper) => {
          // Cleanup bei Zerstörung
          this.cleanupNavigationHandlers(sectionId);
        }
      }
    };

    // Swiper erstellen mit Error Handling
    try {
      console.log('Erstelle Swiper mit Konfiguration:', swiperConfig);
      console.log('Swiper Container:', swiperContainer);
      console.log('Navigation Buttons vor Swiper-Erstellung:', { nextButton, prevButton });

      const swiper = new Swiper(swiperContainer, swiperConfig);
      this.swipers.set(sectionId, swiper);

      // Loading-State entfernen
      swiperContainer.classList.remove('loading');

      console.log('Swiper erfolgreich erstellt:', swiper);

      // Resize-Handler für responsive Updates
      window.addEventListener('resize', this.debounce(() => {
        if (swiper && !swiper.destroyed) {
          // Responsive Auto-Height: Mobile = true, Desktop = false
          const shouldUseAutoHeight = window.innerWidth <= 749;

          if (swiper.autoHeight !== shouldUseAutoHeight) {
            // Auto-Height-Einstellung hat sich geändert
            swiper.autoHeight = shouldUseAutoHeight;

            if (shouldUseAutoHeight) {
              swiper.updateAutoHeight();
            }
          }

          swiper.update();

          // Auto-Height Update nach Resize (nur wenn aktiviert)
          if (swiper.autoHeight) {
            swiper.updateAutoHeight();
          }
        }
      }, 250));

      if (this.debug) {
        console.log('Swiper erfolgreich initialisiert für Section:', sectionId, 'Instance:', swiper);
      }
    } catch (error) {
      if (this.debug) {
        console.error('Fehler beim Erstellen des Swipers:', error);
      }
      swiperContainer.classList.remove('loading');
      this.showStaticLayout(section);
    }
  }



  // Neue Hilfsmethoden für verbesserte Navigation
  testNavigation(swiper, sectionId) {
    console.log('Teste Navigation für Section:', sectionId);

    const navigationWorking = swiper.navigation &&
                             swiper.navigation.nextEl &&
                             swiper.navigation.prevEl &&
                             swiper.navigation.enabled;

    console.log('Navigation Test Ergebnis:', {
      working: navigationWorking,
      nextEl: swiper.navigation?.nextEl,
      prevEl: swiper.navigation?.prevEl,
      enabled: swiper.navigation?.enabled
    });

    if (!navigationWorking) {
      console.warn('Navigation funktioniert nicht korrekt, aktiviere Fallback');
      const nextButton = swiper.navigation?.nextEl;
      const prevButton = swiper.navigation?.prevEl;
      this.setupNavigationFallback(swiper, nextButton, prevButton, sectionId);
    }
  }

  setupNavigationFallback(swiper, nextButton, prevButton, sectionId) {
    if (!nextButton || !prevButton || !swiper) {
      console.warn('Fallback Navigation kann nicht eingerichtet werden - fehlende Elemente');
      return;
    }

    console.log('Richte Fallback Navigation ein für Section:', sectionId);

    // Fallback: Manuelle Event-Listener
    const handleNext = (e) => {
      e.preventDefault();
      e.stopPropagation();
      console.log('Manual Next Click für Section:', sectionId, 'Current Index:', swiper.activeIndex);
      if (swiper && !swiper.destroyed) {
        swiper.slideNext();
        console.log('Nach slideNext - Index:', swiper.activeIndex);
      }
    };

    const handlePrev = (e) => {
      e.preventDefault();
      e.stopPropagation();
      console.log('Manual Prev Click für Section:', sectionId, 'Current Index:', swiper.activeIndex);
      if (swiper && !swiper.destroyed) {
        swiper.slidePrev();
        console.log('Nach slidePrev - Index:', swiper.activeIndex);
      }
    };

    // Entferne alte Event-Listener falls vorhanden
    this.cleanupNavigationHandlers(sectionId);

    // Füge neue Event-Listener hinzu
    nextButton.addEventListener('click', handleNext);
    prevButton.addEventListener('click', handlePrev);

    // Speichere die Handler für späteres Cleanup
    this.navigationHandlers = this.navigationHandlers || new Map();
    this.navigationHandlers.set(sectionId, {
      handleNext,
      handlePrev,
      nextButton,
      prevButton
    });

    console.log('Fallback Navigation erfolgreich eingerichtet für Section:', sectionId);
  }

  cleanupNavigationHandlers(sectionId) {
    if (this.navigationHandlers && this.navigationHandlers.has(sectionId)) {
      const handlers = this.navigationHandlers.get(sectionId);
      if (handlers.nextButton && handlers.handleNext) {
        handlers.nextButton.removeEventListener('click', handlers.handleNext);
      }
      if (handlers.prevButton && handlers.handlePrev) {
        handlers.prevButton.removeEventListener('click', handlers.handlePrev);
      }
      this.navigationHandlers.delete(sectionId);
      console.log('Navigation Handler cleanup für Section:', sectionId);
    }
  }

  initParallax(section) {
    const parallaxElement = section.querySelector('.schmankerl-des-monats__background--parallax');
    if (!parallaxElement) return;

    const parallaxStrength = parseFloat(section.dataset.parallaxStrength) || 50;

    const updateParallax = () => {
      const rect = section.getBoundingClientRect();
      const scrolled = window.pageYOffset;
      const rate = scrolled * -0.5 * (parallaxStrength / 100);

      if (rect.bottom >= 0 && rect.top <= window.innerHeight) {
        parallaxElement.style.transform = `translate3d(0, ${rate}px, 0)`;
      }
    };

    // Throttled scroll event
    let ticking = false;
    const handleScroll = () => {
      if (!ticking) {
        requestAnimationFrame(() => {
          updateParallax();
          ticking = false;
        });
        ticking = true;
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
  }

  enhanceAccessibility(section) {
    // ARIA Labels für Produktkarten
    const productCards = section.querySelectorAll('.schmankerl-des-monats__product-card');
    productCards.forEach((card, index) => {
      card.setAttribute('role', 'article');
      card.setAttribute('aria-label', `Produkt ${index + 1}`);
    });

    // Focus Management für Slider
    const slider = section.querySelector('.schmankerl-swiper');
    if (slider) {
      slider.setAttribute('role', 'region');
      slider.setAttribute('aria-label', 'Produktslider');
    }
  }

  updateSliderAccessibility(section) {
    const activeSlide = section.querySelector('.swiper-slide-active');
    if (activeSlide) {
      // Focus auf aktive Slide setzen wenn nötig
      const focusableElements = activeSlide.querySelectorAll('a, button, [tabindex]:not([tabindex="-1"])');
      focusableElements.forEach(el => el.removeAttribute('tabindex'));
    }

    // Inaktive Slides für Screen Reader verstecken
    const inactiveSlides = section.querySelectorAll('.swiper-slide:not(.swiper-slide-active)');
    inactiveSlides.forEach(slide => {
      const focusableElements = slide.querySelectorAll('a, button');
      focusableElements.forEach(el => el.setAttribute('tabindex', '-1'));
    });
  }

  destroySection(section) {
    if (!section) return;

    const sectionId = section.dataset.sectionId;

    // Navigation Handler cleanup
    this.cleanupNavigationHandlers(sectionId);

    // Swiper zerstören
    if (this.swipers.has(sectionId)) {
      this.swipers.get(sectionId).destroy(true, true);
      this.swipers.delete(sectionId);
    }

    console.log('Section zerstört:', sectionId);
  }

  // Utility: Debounce function
  debounce(func, wait) {
    let timeout;
    return function executedFunction(...args) {
      const later = () => {
        clearTimeout(timeout);
        func(...args);
      };
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
    };
  }
}

// Export für das Build-System
// Globale Verfügbarkeit
window.SchmankerDesMonats = SchmankerDesMonats;
